import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { dataLayer } from '@/utils/dataLayer';
import { Container, Loader } from '@pw-tech/omni-ui';
import { RegistrationStatus } from '@/modules/common/enums';
import useIsMobile from '@/hooks/useIsMobile';

// Importing Step Components
import SideBar from '../SideBar/SideBar';
import Children from '../Children/Children';
import BasicDetails from './BasicDetails/BasicDetails';
import ApplicationFee from './ApplicationFee';
import UploadDocuments from './UploadDocuments/UploadDocuments';
import Cee from './Cee';
import Interview from './Interview';
import CounsellingPreference from './CounsellingPreference/CounsellingPreference';
import FinalResult from './FinalResult';
import AdmissionFee from './AdmissionFee';
import SemesterFee from './SemesterFee';
const defaultSteps = [{
  key: '1',
  label: 'Basic Details',
  description: 'Basic Details',
  component: BasicDetails
}, {
  key: '2',
  label: 'Pay Application Fee',
  description: 'Pay Application Fee',
  component: ApplicationFee
}, {
  key: '3',
  label: 'Upload Documents',
  description: 'Upload Documents',
  component: UploadDocuments
}, {
  key: '4',
  label: 'Entrance Exam (CEE)',
  description: 'Entrance Exam (CEE)',
  component: Cee
}, {
  key: '5',
  label: 'Counselling Preference',
  description: 'Counselling Preference',
  component: CounsellingPreference
}, {
  key: '6',
  label: 'Final Result',
  description: 'Final Result',
  component: FinalResult
}, {
  key: '7',
  label: 'Pay Admission Fee',
  description: 'Pay Admission Fee',
  component: AdmissionFee
}, {
  key: '8',
  label: 'Semester Fee',
  description: 'Semester Fee',
  component: SemesterFee
}];
const RegisterForm = () => {
  // Hooks
  const router = useRouter();
  const {
    courseId,
    step,
    coming_from_page,
    section
  } = router.query;
  const utmQuery = JSON.parse(sessionStorage.getItem('query') || '{}');
  const isMobileScreen = useIsMobile();

  // Context
  const {
    registrationDetails,
    setRegistrationDetails,
    setIsFormSubmitting
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  useEffect(() => {
    if (router.isReady) {
      dataLayer({
        event: 'ioi_step_one_visits',
        coming_from_page: coming_from_page ?? '',
        section: section ?? '',
        ...utmQuery
      });
    }
  }, [router.isReady, coming_from_page, section, utmQuery]);

  // Derive form steps based on registration status and course data
  const formSteps = useMemo(() => {
    const newSteps = [...defaultSteps];
    const skipCee = selectedCourseData?.skipCeeExam;
    if (skipCee) {
      // For MBA: Remove CEE step and always show Interview step
      const ceeIndex = newSteps.findIndex(s => s.component === Cee);
      if (ceeIndex !== -1) {
        newSteps.splice(ceeIndex, 1);
      }

      // Insert Interview step after Upload Documents (index 2 after CEE removal = position 3)
      const uploadDocsIndex = newSteps.findIndex(s => s.component === UploadDocuments);
      if (uploadDocsIndex !== -1) {
        newSteps.splice(uploadDocsIndex + 1, 0, {
          key: (uploadDocsIndex + 2).toString(),
          label: 'Interview',
          description: 'Interview',
          component: Interview
        });
      }

      // Update step numbers
      for (let i = 0; i < newSteps.length; i += 1) {
        newSteps[i].key = (i + 1).toString();
      }
    } else {
      // Rename step 4 if eligible for direct admission
      if (registrationDetails?.isEligibleForDirectAdmission) {
        const step4Index = newSteps.findIndex(s => s.component === Cee);
        if (step4Index !== -1) {
          newSteps[step4Index] = {
            ...newSteps[step4Index],
            label: 'Fast Track Process',
            description: 'Fast Track Process'
          };
        }
      }
      if (registrationDetails?.cee_status === RegistrationStatus.cee_passed_excellence || registrationDetails?.da_status === RegistrationStatus.da_passed_excellence) {
        newSteps.splice(4, 0, {
          key: '5',
          label: 'Interview',
          description: 'Interview',
          component: Interview
        });

        // Update step numbers for the shifted steps
        for (let i = 4; i < newSteps.length; i += 1) {
          newSteps[i].key = (i + 1).toString();
        }
      }
    }
    return newSteps;
  }, [registrationDetails, selectedCourseData]);

  // Guard: keep user on Counselling Preference step until they select one
  useEffect(() => {
    if (!router.isReady || !registrationDetails?._id) return;
    if (registrationDetails?.counsellingPreference) return;
    const counsellingIndex = formSteps.findIndex(s => s.component === CounsellingPreference);
    if (counsellingIndex === -1) return;
    const counsellingStep = counsellingIndex + 1;
    const activeStep = Number(router.query.step ?? registrationDetails?.step ?? 1);
    if (Number.isFinite(activeStep) && activeStep > counsellingStep) {
      router.replace({
        pathname: '/register',
        query: {
          ...router.query,
          step: counsellingStep
        }
      });
    }
    // router is stable in Next.js — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.step, registrationDetails?._id, registrationDetails?.step, registrationDetails?.counsellingPreference, formSteps]);
  return <div className="w-full h-full" data-sentry-component="RegisterForm" data-sentry-source-file="index.tsx">
      {registrationDetails?._id && selectedCourseData?._id ? <>
          {isMobileScreen ? <div className="flex flex-col sm:!flex-row h-full w-full">
              <div className="sm:!flex-shrink-0">
                <SideBar formSteps={formSteps} />
              </div>

              {/* Dynamic Content Pages */}
              <div className="w-full overflow-auto py-2 sm:!py-0">
                <Container className="mx-auto px-4 !md:px-0">
                  <Children formSteps={formSteps} />
                </Container>
              </div>
            </div> : <Container className="mx-auto px-4 !md:px-0 h-full">
              <div className="flex flex-col sm:!flex-row h-full w-full">
                <div className="sm:!flex-shrink-0 w-1/4 mr-11">
                  <SideBar formSteps={formSteps} />
                </div>
                {/* Dynamic Content Pages */}
                <div className="w-full overflow-auto py-2 sm:!py-0">
                  <Children formSteps={formSteps} />
                </div>
              </div>
            </Container>}
        </> : <>
          <div className="flex items-center justify-center h-full">
            <Loader message="Loading..." />
          </div>
        </>}
    </div>;
};
export default RegisterForm;